import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Check, Users } from 'lucide-react';
import type { Classroom } from '../../../accounts';
import { useClickOutside } from '../../../app/hooks/useClickOutside';
import { useEscapeKey } from '../../../app/hooks/useEscapeKey';
import { CoBadge } from './CoBadge';
interface ClassroomSwitcherProps {
  classrooms: Classroom[];
  selectedClassroomId: string | null;
  onSelectClassroom: (id: string | null) => void;
  /** Show a label like "Klas" before the value. Default: true */
  showLabel?: boolean;
  /** Visual size. Default: 'sm' */
  size?: 'sm' | 'md';
  /** Hide entirely when there's only one classroom (just shows the name as static label). Default: true */
  hideWhenSingle?: boolean;
  /** Active teacher account id — used to mark co-teacher classrooms with a 'Co' badge. */
  activeTeacherId?: string;
}
/**
 * Page-header classroom selector. Mirrors the sidebar dropdown but lives
 * inline next to a page title so teachers on tablet/mobile can switch
 * classrooms without opening the drawer.
 *
 * No Djay — this is operational UI (Zone 2 NO per Djay framework).
 */
export function ClassroomSwitcher({
  classrooms,
  selectedClassroomId,
  onSelectClassroom,
  showLabel = true,
  size = 'sm',
  hideWhenSingle = true,
  activeTeacherId
}: ClassroomSwitcherProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  useClickOutside(containerRef, () => {
    if (open) setOpen(false);
  });
  useEscapeKey(() => {
    if (open) setOpen(false);
  });
  if (classrooms.length === 0) return null;
  const active = selectedClassroomId ? classrooms.find(c => c.id === selectedClassroomId) : null;
  const display = active ? active.name : 'Alle klassen';
  const learnerCount = active ? active.learnerIds.length : null;
  const isCoTeach = (c: Classroom) => !!activeTeacherId && c.teacherId !== activeTeacherId;
  // Static label fallback for single-classroom teachers
  if (hideWhenSingle && classrooms.length === 1) {
    const only = classrooms[0];
    return <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-600" data-id="element-8981" data-mp="1u4bqfvym11rw">
        <Users className="w-3.5 h-3.5 text-slate-400" data-id="element-8982" data-mp="1f8e0wwj6yvon" />
        <span data-id="element-8983" data-mp="1k5vyxrvo7qhv">{only.name}</span>
        {isCoTeach(only) && <CoBadge data-id="element-8984" data-mp="27uowttvus8f4" />}
        <span className="text-slate-400 font-medium" data-id="element-8985" data-mp="27u5iso9bq46d">
          · {only.learnerIds.length}
        </span>
      </div>;
  }
  const buttonSize = size === 'md' ? 'px-4 py-2.5 text-sm' : 'px-3 py-2 text-xs';
  return <div ref={containerRef} className="relative inline-block" data-id="element-8986" data-mp="1y3ue2zhtfqyf">
      <button type="button" onClick={() => setOpen(v => !v)} aria-haspopup="listbox" aria-expanded={open} className={`${buttonSize} inline-flex items-center gap-2 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg font-bold text-slate-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 shadow-sm`} data-id="element-8987" data-mp="233p30xi166gu">
        <Users className="w-3.5 h-3.5 text-slate-400" data-id="element-8988" data-mp="f6pnlryuh9fi" />
        {showLabel && <span className="text-slate-400 font-medium" data-id="element-8989" data-mp="f4d7zlgk7b5m">Klas:</span>}
        <span className="truncate max-w-[160px] text-slate-900" data-id="element-8990" data-mp="ttsng5an3u60">{display}</span>
        {active && isCoTeach(active) && <CoBadge data-id="element-8991" data-mp="owspyiruvzqi" />}
        {learnerCount !== null && <span className="text-slate-400 font-medium" data-id="element-8992" data-mp="15myt7ggtl0w">· {learnerCount}</span>}
        <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-8993" data-mp="3sczknzsammn5" />
      </button>

      <AnimatePresence data-id="element-8994" data-mp="a8o3d3pon42g">
        {open && <motion.div initial={{
        opacity: 0,
        y: -4
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -4
      }} transition={{
        duration: 0.12
      }} className="absolute top-full right-0 mt-2 w-64 bg-white border border-slate-200 rounded-xl shadow-lg z-30 overflow-hidden py-1.5" role="listbox" aria-label="Klas selecteren" data-id="element-8995" data-mp="65ri158rctut">
            <button onClick={() => {
          onSelectClassroom(null);
          setOpen(false);
        }} role="option" aria-selected={selectedClassroomId === null} className={`w-full flex items-center justify-between px-4 py-2.5 text-sm hover:bg-slate-50 transition-colors ${selectedClassroomId === null ? 'text-indigo-700 font-bold bg-indigo-50/50' : 'text-slate-700 font-medium'}`} data-id="element-8996" data-mp="2lxm7n3xyfb5q">
              <span data-id="element-8997" data-mp="39jsqkl8x2baq">Alle klassen</span>
              {selectedClassroomId === null && <Check className="w-4 h-4" data-id="element-8998" data-mp="3ekqeleglxzk5" />}
            </button>
            <div className="h-px bg-slate-100 my-1.5 mx-4" data-id="element-8999" data-mp="3eidr6ls2ywzp" />
            {classrooms.map(c => <button key={c.id} onClick={() => {
          onSelectClassroom(c.id);
          setOpen(false);
        }} role="option" aria-selected={selectedClassroomId === c.id} className={`w-full flex items-center justify-between px-4 py-2.5 text-sm hover:bg-slate-50 transition-colors ${selectedClassroomId === c.id ? 'text-indigo-700 font-bold bg-indigo-50/50' : 'text-slate-700 font-medium'}`} data-id="element-9000" data-mp="3jgxbyiuxu2qh">
                <div className="flex items-center gap-2 truncate min-w-0" data-id="element-9001" data-mp="1lnmlsrrt8nwt">
                  <span className="truncate" data-id="element-9002" data-mp="1lpp7izlxjuvq">{c.name}</span>
                  {isCoTeach(c) && <CoBadge data-id="element-9003" data-mp="1gutf3ycs75i3" />}
                  <span className="text-[10px] py-0.5 px-2 bg-slate-100 text-slate-500 rounded-md font-bold flex-shrink-0" data-id="element-9004" data-mp="1bxjhhvf8nnj6">
                    {c.learnerIds.length}
                  </span>
                </div>
                {selectedClassroomId === c.id && <Check className="w-4 h-4 flex-shrink-0" data-id="element-9005" data-mp="24knna2m5o5w0" />}
              </button>)}
          </motion.div>}
      </AnimatePresence>
    </div>;
}